/**
 * 基于 Element UI 表单验证封装的工具类。验证器必须返回一个方法，异常情况需要抛出一个异常
 */
export default class Validator {
  constructor(rule, value, cb) {
    this.rule = rule;
    this.value = value;
    this.cb = cb;
    this.rules = [];
  }

  /**
   * 添加一个内部验证器
   * @param rule
   * @returns {Validator}
   */
  register(rule) {
    this.rules.push(rule);
    return this;
  }

  /**
   * 添加一个自定义验证器，要求返回一个方法
   * @param fn
   * @returns {Validator}
   */
  define(fn) {
    this.rules.push(fn(this.rule, this.value, this.cb));
    return this;
  }

  /**
   * 进行验证
   * @param done 如果该值为 truly，则在验证通过后直接执行 cb 回调
   */
  validate(done = true) {
    const result = this.rules.every(item => {
      try {
        item();
        return true;
      } catch (e) {
        this.cb(e);
        return false;
      }
    });

    if (result && done) this.cb();
  }

  /**
   * 要求必填
   */
  required(message = `该字段不能为空`) {
    return () => {
      if (!this.value.trim()) {
        throw new Error(message);
      }
    };
  }

  /**
   * 只能输入字母、数字或下划线
   */
  isStandardStr(message = `只能输入字母、数字或下划线`) {
    return () => {
      if (!/^[a-zA-Z0-9_]+$/g.test(this.value)) {
        throw new Error(message);
      }
    };
  }

  /**
   * 只能输入字母、数字、下划线或中文
   */
  isNameStandardStr(message = `只能输入字母、数字、下划线或中文`) {
    return () => {
      if (!/^[\u4e00-\u9fa5_a-zA-Z0-9]+$/g.test(this.value)) {
        throw new Error(message);
      }
    };
  }

  /**
   * 不能以数字开头
   */
  isNotStartsWithNumber(message = `不能以数字开头`) {
    return () => {
      if (/^[0-9]+/g.test(this.value)) {
        throw new Error(message);
      }
    };
  }

  /**
   * 要求不能输入空格
   */
  isNoSpace(message = `不能包含空格`) {
    return () => {
      if (/\s/g.test(this.value)) {
        throw new Error(message);
      }
    };
  }

  /**
   * 要求值不相等
   */
  isNotEqual(val, message = `两个值不能相同`) {
    return () => {
      if (this.value === val) {
        throw new Error(message);
      }
    };
  }

  /**
   * 长度范围
   */
  lenRange(length, message = `最多可以输入 ${length} 个字符`) {
    return () => {
      if (this.value && this.value.length > length) {
        throw new Error(message);
      }
    };
  }

  /**
   * 要求必须以 / 开头
   */
  isStartsWithSlash(message = `必须以 / 开头`) {
    return () => {
      if (!/^\/[\d\D]*/.test(this.value)) {
        throw new Error(message);
      }
    };
  }

  /**
   * nginx 日志配置规则
   */
  isStandardNginx(val, message = `NGINX 日志配置格式不正确`) {
    return () => {
      if (!/^log_format[\d\D]+/.test(this.value)) {
        throw new Error(message);
      }
    };
  }

  /**
   * 手机号验证
   */
  isMobile(message = `手机号输入有误`) {
    return () => {
      if (this.value && !/^1[3456789]\d{9}$/.test(this.value)) {
        throw new Error(message);
      }
    };
  }

  /**
   * 最小长度范围
   */
  minLenRange(length, message = `长度不能小于 ${length}`) {
    return () => {
      if (this.value.length < length) {
        throw new Error(message);
      }
    };
  }

  /**
   * 时间是否符合查询范围
   */
  isConform(val1, val2, message = `时间范围不可以超过 2 个小时，请重新选择`) {
    return () => {
      if (val1 === 'hours' && Number(val2) > 2) {
        throw new Error(message);
      }
      if (val1 === 'minutes' && Number(val2) > 120) {
        throw new Error(message);
      }
      if (val1 === 'seconds' && Number(val2) > 7200) {
        throw new Error(message);
      }
    };
  }

  /**
   * 两次告警间隔的最小时间
   */
  isIntervalTime(val1, val2, message = `间隔时间不可以超过 24 小时，请重新选择`) {
    return () => {
      if (val1 === 'hours' && Number(val2) > 24) {
        throw new Error(message);
      }
      if (val1 === 'minutes' && Number(val2) > 1440) {
        throw new Error(message);
      }
    };
  }
  /**
   * 数字最大值限制
   */
  maxNumRange(num, message = `数字不能大于 {num}`) {
    return () => {
      if (this.value > num) {
        throw new Error(message);
      }
    };
  }

  /**
   * 不可输入中文
   */
  isIncludeChinese(val, message = `sql 触发条件中不可包含中文`) {
    return () => {
      if (/[\u4E00-\u9FA5]/g.test(this.value)) {
        throw new Error(message);
      }
    };
  }

  /**
   * 只能输入数字
   */
  isOnlyNumber(val, message = `只能输入数字`) {
    return () => {
      if (!/^[0-9]+$/g.test(val)) {
        throw new Error(message);
      }
    };
  }
}
